// Web Push subscription + service-worker registration.
// Mirrors legacy initPush / subscribePush flow: registers /sw.js (shared with
// the legacy UI at root scope), fetches VAPID public key, subscribes via
// pushManager, and posts the subscription to /push/subscribe.
import { apiGet, apiPost } from "@/lib/api-client";

function base64UrlToUint8Array(b64: string): Uint8Array {
  const raw = atob(b64.replace(/-/g, "+").replace(/_/g, "/"));
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported(): boolean {
  return typeof navigator !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;
}

/** Register the shared SW at root scope so notifications work across / and /legacy. */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!pushSupported()) return null;
  try {
    // /sw.js is served from the Worker asset root by wrangler.toml.
    return await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  } catch (e) {
    console.warn("SW register failed:", e);
    return null;
  }
}

interface VapidKeyResponse { key?: string }

export async function subscribePush(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    const { key } = await apiGet<VapidKeyResponse>("/push/vapid-public-key");
    if (!key) return null;

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      // Cast through unknown because TS5's DOM lib types PushSubscriptionOptions
      // tightly to ArrayBuffer, but browsers accept any BufferSource.
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: base64UrlToUint8Array(key) as unknown as BufferSource,
      });
    }

    const subJson = sub.toJSON() as { keys?: { p256dh?: string; auth?: string } };
    await apiPost("/push/subscribe", {
      endpoint: sub.endpoint,
      p256dh: subJson.keys?.p256dh,
      auth: subJson.keys?.auth,
    });
    return sub;
  } catch (e) {
    console.warn("Push subscribe failed:", e);
    return null;
  }
}

/** Prompt the user for permission (if not already decided) then subscribe. */
export async function requestAndSubscribe(): Promise<NotificationPermission> {
  if (!pushSupported()) return "denied";
  const perm = await Notification.requestPermission();
  if (perm === "granted") await subscribePush();
  return perm;
}
